import { AlertIconLight } from "@/assets/images/svgIcons/alertIcon"
import { CompleteSessionIcon } from "@/assets/images/svgIcons/sessions/completeSessionIcon"
import { WinningCupIcon } from "@/assets/images/svgIcons/winningCupIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { useProgramSessionActions } from "@/modules/sessions/hooks/useProgramSessionActions"
import { useSetSessionCompleted } from "@/modules/sessions/hooks/useSetSessionCompleted"
import { ExerciseDetail } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/components/exerciseDetail"
import { MediaDetails } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetails"
import { NoteForAthletesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForAthletesDetails"
import { NoteForCoachesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForCoachesDetails"
import { SessionDetailsContentFullscreen } from "@/modules/sessions/sessionDetails/components/sessionDetailsContentFullscreen"
import { createSessionDetailsContentModel } from "@/modules/sessions/sessionDetails/models/sessionDetailsContentModel"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSessionCopyActions } from "@/modules/sessions/stores/useSessionCopyStore"
import { SocialCard } from "@/modules/social/components/socialCard"
import { useCreateSessionTemplate } from "@/modules/template/hooks/useTemplate"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import SessionEmpty from "@images/empty/session_empty.svg"
import SessionHideEmpty from "@images/empty/session_hide_empty.svg"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { GlobeIcon } from "@images/svgIcons/globeIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { LockIcon } from "@images/svgIcons/lockIcon"
import RestDayIcon from "@images/svgIcons/restDayIcon.svg"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramSubscriptionStatus, ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { ScoreboardCard } from "@modules/scores/scoreboard/components/scoreboardCard"
import { useScoreboardSidebar } from "@modules/scores/scoreboard/hooks/useScoreboardSidebar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { EmptyView } from "@modules/ui/components/emptyView"
import { FullscreenModal, type FullscreenModalRef } from "@modules/ui/components/fullScreenModal/fullscreenModal"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { EditIcon } from "@modules/ui/icons/editIcon"
import { CircleXIcon, ClipboardCheckIcon, ExpandIcon } from "lucide-react"
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react"
import { Link } from "react-router"

type SessionDetailsContentProps = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  programId: string
  programPath: string
  allowEditContent: boolean
  refreshTraining?: () => void
  className?: string
} & React.ComponentProps<"div">

export function SessionDetailsContent({
  session,
  programId,
  programPath,
  allowEditContent,
  refreshTraining,
  className,
  ...props
}: SessionDetailsContentProps) {
  const [displayCongratulations, setDisplayCongratulations] = useState<boolean>(false)

  const { setSessionCopy } = useSessionCopyActions()
  const language = useLanguage()
  const confirmPopup = useConfirmPopup()
  const { hideSidebar, setCustomHeaderButtons } = useSidebar()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const { data: program } = useProgram(programId)
  const { getBlockDurationInterval, timedBlocks, timedBlocksTotal } = useBlockDuration(session.blocks)
  const { navigateToSessionForm } = useSessionSidebar()
  const { deleteSession } = useProgramSessionActions()
  const { mutateAsync: createSessionTemplate } = useCreateSessionTemplate()
  const { setSessionCompleted, isPending: isSetSessionCompletedPending } = useSetSessionCompleted()
  const mySpaceNavigate = useMySpaceNavigate()

  const fullscreenModalRef = useRef<FullscreenModalRef>(null)
  const onEditSessionRef = useRef<() => void>(() => {
    /* empty */
  })
  const onDeleteSessionConfirmationPopupRef = useRef<() => void>(() => {
    /* empty */
  })
  const onSessionCopyRef = useRef<() => void>(() => {
    /* empty */
  })

  const { navigateToScoreboardView } = useScoreboardSidebar()
  const { data: progression } = useProgramOnOffProgression(
    programId,
    program.type !== ProgramType.ONOFF ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )

  const {
    sessionDate,
    isLastOnOffProgramSession,
    canUserSeeSessionContent,
    canUserEditSession,
    canSeeScoreboardCard,
    canUserAccessToScoreboard,
    canUserCompleteExercisesScore,
    canUserToggleSessionCompleted,
  } = useMemo(
    () =>
      createSessionDetailsContentModel(
        program,
        session,
        progression,
        isAuthor,
        isAcceptedSharedCoach,
        allowEditContent,
      ),
    [program, session, progression, language, isAuthor, isAcceptedSharedCoach, allowEditContent],
  )

  const onClickOnScoreboard = () => {
    if (isAuthor || isAcceptedSharedCoach || canUserAccessToScoreboard) {
      navigateToScoreboardView(session)
    } else {
      toast.error(word("session.scoreboard.card.errorMessage"), {
        classNames: errorToastClassName(),
      })
    }
  }

  /* Session edit */
  const editSession = useEffectEvent(() => {
    void navigateToSessionForm(program, session, undefined, undefined, { refreshCalendarEvents: refreshTraining })
  })

  const onToggleSessionCompleted = useCallback(() => {
    const nextCompleted = !session.sessionCompletedByUser
    setSessionCompleted({ session: session, completed: nextCompleted })
      .then(() => {
        if (nextCompleted && isLastOnOffProgramSession) {
          setDisplayCongratulations(true)
        } else if (nextCompleted) {
          toast.success(word("session.validation.setCompleted.success"), {
            classNames: successToastClassName(),
          })
        } else {
          toast.success(word("session.validation.setNotCompleted.success"), {
            classNames: successToastClassName(),
          })
        }
      })
      .catch(() => {
        toast.error(word("global.error.label"), {
          classNames: errorToastClassName(),
        })
      })
  }, [session, setSessionCompleted, isLastOnOffProgramSession])

  useEffect(() => {
    onEditSessionRef.current = () => {
      editSession()
    }
  }, [])

  /* Session delete */
  const confirmationModaleContent = useMemo(
    () => (
      <div className="flex w-full flex-col items-center gap-2">
        <div className="mb-4 flex w-full flex-col items-center">
          <AlertIconLight color="currentColor" className="text-textError" />
          <Text fontStyle="TL">{word("sessionDetailed.deleteSession.popup.title")}</Text>
        </div>
        <Text fontStyle="BM" className="break-all">
          {word("sessionDetailed.deleteSession.popup.message", {
            sessionName: session.name,
          })}
        </Text>
      </div>
    ),
    [session.name],
  )

  const deleteSessionConfirmationPopup = useCallback(() => {
    confirmPopup.show({
      title: "",
      message: confirmationModaleContent,
      accept: async () => {
        try {
          await deleteSession({ session })

          toast.success(word("program.session.delete.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar()
          if (refreshTraining) refreshTraining()
        } catch {
          toast.error(word("global.error.label"), {
            classNames: errorToastClassName(),
          })
        }
      },
      footerProps: {
        cancelText: word("global.cancel"),
        confirmText: word("global.delete"),
      },
    })
  }, [confirmPopup, deleteSession, hideSidebar, refreshTraining, confirmationModaleContent, session])

  useEffect(() => {
    onDeleteSessionConfirmationPopupRef.current = deleteSessionConfirmationPopup
  }, [deleteSessionConfirmationPopup])

  /* Session copy */
  const onSessionCopy = useCallback(() => {
    try {
      setSessionCopy(session)
      toast.success(word("program.session.copy.success"), {
        classNames: successToastClassName(),
      })
    } catch {
      toast.error(word("program.session.copy.error"), {
        classNames: errorToastClassName(),
      })
    }
  }, [setSessionCopy, session])

  useEffect(() => {
    onSessionCopyRef.current = onSessionCopy
  }, [onSessionCopy])

  useEffect(() => {
    const customHeaderIcons = []
    if (canUserEditSession || canUserToggleSessionCompleted) {
      customHeaderIcons.push(
        <EllipsisMenu>
          {canUserEditSession && (
            <DropdownMenuItem
              key="edit"
              onClick={() => {
                onEditSessionRef.current()
              }}
            >
              <EditIcon color="currentColor" className="text-textDefault-heading" />
              {word("global.edit")}
            </DropdownMenuItem>
          )}
          {canUserEditSession && (
            <DropdownMenuItem
              key="copy"
              onClick={() => {
                onSessionCopyRef.current()
              }}
            >
              <CopyIcon color="currentColor" className="text-textDefault-heading" size={20} />
              {word("program.session.copy.label")}
            </DropdownMenuItem>
          )}
          {canUserEditSession && (
            <DropdownMenuItem
              key="saveAsTemplate"
              onClick={() =>
                void createSessionTemplate({
                  name: session.name,
                  description: null,
                  templateCategoryId: null,
                  sessionId: session.id,
                })
                  .then(() => {
                    toast.success(word("template.toast.created.success"), {
                      classNames: successToastClassName(),
                    })
                  })
                  .catch(() => {
                    toast.error(word("template.toast.created.error"), {
                      classNames: errorToastClassName(),
                    })
                  })
              }
            >
              <ClipboardCheckIcon color="currentColor" className="text-textDefault-heading" size={20} />
              {word("template.session.text.saveAs")}
            </DropdownMenuItem>
          )}
          {canUserEditSession && program.type !== ProgramType.ONOFF && (
            <DropdownMenuItem
              key="delete"
              onClick={() => {
                onDeleteSessionConfirmationPopupRef.current()
              }}
            >
              <TrashIcon color="currentColor" className="text-textDefault-heading" size={20} />
              {word("global.delete")}
            </DropdownMenuItem>
          )}
          {canUserEditSession &&
            session.blocks.length > 0 &&
            session.blocks.filter((block) => block.type === SessionBlockType.SESSION_EXERCISE).length > 0 && (
              <DropdownMenuItem key="fullscreen" onClick={() => fullscreenModalRef.current?.open()}>
                <ExpandIcon color="currentColor" className="text-textDefault-heading" size={20} />
                {word("program.session.displayFullScreen")}
              </DropdownMenuItem>
            )}
          {canUserToggleSessionCompleted && (
            <DropdownMenuItem
              key="complete"
              onClick={() => {
                onToggleSessionCompleted()
              }}
            >
              {session.sessionCompletedByUser ? (
                <CircleXIcon color="currentColor" width={20} height={20} />
              ) : (
                <CompleteSessionIcon color="currentColor" width={20} height={20} />
              )}
              {session.sessionCompletedByUser
                ? word("session.validation.setNotCompleted.shortLabel")
                : word("session.validation.setCompleted.label")}
            </DropdownMenuItem>
          )}
        </EllipsisMenu>,
      )
    }

    if (customHeaderIcons.length > 0) {
      setCustomHeaderButtons(customHeaderIcons)
    }
  }, [canUserEditSession, canUserToggleSessionCompleted, session])

  return (
    <div className={cn("relative flex w-full grow flex-col gap-4", className)} {...props}>
      <FullscreenModal ref={fullscreenModalRef}>
        <SessionDetailsContentFullscreen session={session} programId={programId} />
      </FullscreenModal>

      <div className="flex max-h-70 min-h-55 w-full overflow-hidden rounded-b-4xl bg-surface-foreground">
        <div className="flex h-full w-5/10 flex-col items-start justify-between gap-2 pt-5 pb-6 pl-6">
          <div className="flex w-full flex-row justify-between gap-2">
            <Text fontStyle="BM" className="text-textDefault-body">
              {sessionDate}
            </Text>
            {canUserSeeSessionContent && (
              <div className="flex gap-2">
                {session.gymnasticTag && <GymSelectedIcon color="" width={25} height={25} />}
                {session.weightliftingTag && <WeightliftingSelectedIcon color="" width={25} height={25} />}
                {session.cardioTag && <CardioSelectedIcon color="" width={25} height={25} />}
              </div>
            )}
          </div>

          <Text fontStyle="HS" className="w-full wrap-break-word text-textDefault-heading">
            {"restDay" in session && session.restDay ? word("program.onOff.restDay") : session.name}
          </Text>

          <div className="flex max-w-full flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <ProfileAvatar
                profile={{ avatar: session.owner.avatar, username: session.owner.username }}
                className="size-9"
              />
              <Text
                fontStyle="LM"
                className="overflow-hidden text-ellipsis text-textDefault-heading"
              >{`${session.owner.firstname} ${session.owner.lastname}`}</Text>
            </div>
            <Link to={programPath} style={{ marginLeft: 9 }}>
              <div className="flex items-center gap-1">
                {program.visibility === ProgramVisibility.PUBLIC ? (
                  <GlobeIcon color="currentColor" className="shrink-0 text-secondary-500" width={16} height={16} />
                ) : (
                  <LockIcon color="currentColor" className="shrink-0 text-secondary-500" width={16} height={16} />
                )}
                <Text fontStyle="LM" className="line-clamp-1">
                  {program.name}
                </Text>
              </div>
            </Link>
          </div>
        </div>
        <div className="relative h-full w-5/10 overflow-hidden rounded-br-2xl [clip-path:polygon(32%_0%,100%_0%,100%_100%,0%_100%)]">
          <div className="absolute inset-0 z-1 flex bg-neutral-200 opacity-80" />
          <img src={program.image?.url} alt="program image" className="z-5 h-full w-full object-cover" />
        </div>
      </div>

      {session.description && (
        <HURichTextView value={formatTextToRichText(session.description)} paragrapheFontStyle="BM" className="mt-2" />
      )}

      <div className="flex grow flex-col gap-4 px-5">
        <div className="shrink-0">
          <SocialCard
            socialPostId={session.socialPostId}
            sessionInfos={{ id: session.id, name: session.name }}
            socialResume={session.socialResume}
          />
        </div>

        {canSeeScoreboardCard && (
          <ScoreboardCard
            session={session}
            onClick={() => onClickOnScoreboard()}
            scoreboardLocked={program.scoreboardLocked}
            canUserAccessToScoreboard={canUserAccessToScoreboard}
            isAuthor={isAuthor}
            isAcceptedSharedCoach={isAcceptedSharedCoach}
            isOwner={false}
          />
        )}

        {program.type === ProgramType.ONOFF && "restDay" in session && session.restDay ? (
          <div className="flex h-129 flex-col items-center justify-center rounded-3xl bg-surface-foreground p-6">
            <img src={RestDayIcon} alt="Rest day" width={150} className="mb-2" />
            <Text fontStyle="TL">{word("program.onOff.restDay")}</Text>
          </div>
        ) : canUserSeeSessionContent && session.blocks.length > 0 ? (
          <div className="flex flex-col gap-5 pt-2">
            <div className="flex flex-row items-center justify-between">
              <Text fontStyle="LS" className="text-textDefault-body uppercase">
                {`${word("program.planning.create.exercise.block_plural", { count: session.blocks.length })} (${session.blocks.length.toString()})`}
              </Text>

              {timedBlocks.length > 0 && (
                <div>
                  <Text fontStyle="LS" className="border-r border-borderDefault pr-1 text-textDefault-body uppercase">
                    {`${word("sessionForm.blocks.timed")} (${timedBlocks.length.toString()})`}
                  </Text>
                  <Text fontStyle="LS" className="pl-1 text-textDefault-body">
                    {word("sessionForm.block.totalTime")}
                    <Text fontStyle="LS" className="pl-1 font-bold">
                      {timedBlocksTotal}
                    </Text>
                    {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                  </Text>
                </div>
              )}
            </div>

            {session.blocks.map((block, blockIndex) => (
              <div key={blockIndex}>
                {(block.type === SessionBlockType.SESSION_EXERCISE || block.type === SessionBlockType.COACH_NOTE) &&
                  !!block.content.durationInMin && (
                    <Text fontStyle="BS" className="mb-1 flex flex-row items-center gap-2">
                      <div className="bg-neutral-700" style={{ width: 10, height: 10, borderRadius: 3 }} />
                      {getBlockDurationInterval(blockIndex)}
                    </Text>
                  )}
                {block.type === SessionBlockType.SESSION_EXERCISE ? (
                  <div key={blockIndex}>
                    <ExerciseDetail
                      exercise={block.content}
                      parentSession={session}
                      canUserCompleteExerciseScore={canUserCompleteExercisesScore}
                      isCoach={isAuthor || isAcceptedSharedCoach}
                    />
                  </div>
                ) : block.type === SessionBlockType.COACH_NOTE ? (
                  <NoteForCoachesDetails key={blockIndex} note={block.content} />
                ) : block.type === SessionBlockType.MEDIAS ? (
                  <MediaDetails key={blockIndex} medias={block.content.medias} />
                ) : (
                  <NoteForAthletesDetails key={blockIndex} note={block.content} />
                )}
              </div>
            ))}
          </div>
        ) : canUserSeeSessionContent && session.blocks.length === 0 ? (
          isAuthor ? (
            <EmptyView
              image={{ src: SessionEmpty }}
              title={word("session.empty.title.noExerciseAuthor")}
              description={word("session.empty.description.noExerciseAuthor")}
              flexGrow={false}
              className="bg-surface-foreground p-6"
              button={
                canUserEditSession && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      onEditSessionRef.current()
                    }}
                  >
                    {word("global.add")}
                  </Button>
                )
              }
              paddingType="In_table"
            />
          ) : (
            <EmptyView
              image={{ src: SessionEmpty }}
              title={word("session.empty.title.noExercise")}
              description={word("session.empty.description.noExercise")}
              flexGrow={false}
              className="bg-surface-foreground p-6"
              button={
                <Button
                  variant="primary"
                  onClick={() => {
                    hideSidebar()
                  }}
                >
                  {word("global.button.wait")}
                </Button>
              }
              paddingType="In_table"
            />
          )
        ) : (
          <EmptyView
            image={{ src: SessionHideEmpty }}
            title={word("session.empty.title.hideSession")}
            description={word("session.empty.description.hideSession")}
            flexGrow={false}
            className="bg-surface-foreground p-6"
            button={
              <Button
                variant="primary"
                onClick={() => {
                  hideSidebar()
                }}
              >
                {word("global.button.wait")}
              </Button>
            }
            paddingType="In_table"
          />
        )}
      </div>

      {canUserToggleSessionCompleted && !session.sessionCompletedByUser && (
        <div className="sticky bottom-0 border-t border-surface-border-gray-muted bg-surface-background-default">
          <div className="mx-auto flex w-full items-center justify-end p-4 pb-2">
            <Button
              variant={"primary"}
              disabled={isSetSessionCompletedPending}
              onClick={() => {
                onToggleSessionCompleted()
              }}
              size="lg"
            >
              <CompleteSessionIcon color="currentColor" width={20} height={20} />
              {word("session.validation.setCompleted.label")}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={displayCongratulations} onOpenChange={setDisplayCongratulations}>
        <DialogContent className="mt-3 flex w-105 flex-col rounded-4xl">
          <DialogHeader className="flex flex-col items-center gap-5">
            <DialogTitle>
              <WinningCupIcon width={150} height={150} color="" />
            </DialogTitle>
            <DialogDescription className="flex flex-col items-center gap-2">
              <Text fontStyle="TL">{word("program.onOff.completed.congratulations.title")}</Text>

              <Text fontStyle="BM" className="mx-3 text-center">
                {word("program.onOff.completed.congratulations.content")}
              </Text>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 flex w-full flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setDisplayCongratulations(false)
              }}
            >
              {word("program.onOff.completed.congratulations.button.close")}
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                mySpaceNavigate(Path.MySpace.Explore)
              }}
            >
              {word("program.onOff.completed.congratulations.button.explore")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
